
import React, { useState } from 'react';
import { Star, ShoppingCart, Eye, Heart, X, Check, Shield, Truck, MessageSquare, Calendar } from 'lucide-react';
import { Product, Review } from '../types';
import { useApp } from '../AppContext';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart, toggleWishlist, wishlist } = useApp();
  const [isHovered, setIsHovered] = useState(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isVariantSelectorOpen, setIsVariantSelectorOpen] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'details' | 'reviews'>('details');
  const isWishlisted = wishlist.includes(product.id);

  const handleQuickViewToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsQuickViewOpen(!isQuickViewOpen);
    setActiveTab('details');
  };

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (product.variants && product.variants.length > 0) {
      setIsVariantSelectorOpen(true);
    } else {
      addToCart(product);
    }
  };

  const confirmQuickAdd = () => {
    if (product.variants) {
      const allSelected = product.variants.every(v => selectedVariants[v.name]);
      if (!allSelected) {
        alert('Please select all options');
        return;
      }
    }
    addToCart(product, selectedVariants);
    setIsVariantSelectorOpen(false);
    setSelectedVariants({});
  };

  return (
    <>
      <div 
        className="group relative bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 dark:border-slate-700"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Badges */}
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
          {product.isNew && (
            <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
              New
            </span>
          )}
          {product.originalPrice && (
            <span className="bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
              Sale
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={(e) => { e.preventDefault(); toggleWishlist(product.id); }}
          className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-all duration-300 ${
            isWishlisted 
              ? 'bg-rose-500 text-white' 
              : 'bg-white/80 dark:bg-slate-700/80 text-slate-400 hover:text-rose-500 backdrop-blur-sm'
          }`}
        >
          <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current' : ''}`} />
        </button>

        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-slate-100 dark:bg-slate-900">
          <img
            src={product.images[0]}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
          />
          
          {/* Quick Action Overlay */}
          <div className={`absolute inset-0 bg-black/20 flex items-center justify-center gap-3 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <button 
              onClick={handleQuickViewToggle}
              className="p-3 bg-white text-slate-900 rounded-full hover:bg-indigo-600 hover:text-white transition-colors shadow-lg translate-y-4 group-hover:translate-y-0 duration-500"
              title="Quick View"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button 
              onClick={handleQuickAdd}
              className="p-3 bg-white text-slate-900 rounded-full hover:bg-indigo-600 hover:text-white transition-colors shadow-lg translate-y-4 group-hover:translate-y-0 duration-500 delay-75"
              title="Quick Add"
            >
              <ShoppingCart className="w-5 h-5" />
            </button>
          </div>

          {/* Variant Selector Overlay */}
          {isVariantSelectorOpen && (
            <div className="absolute inset-0 z-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm p-4 flex flex-col animate-in fade-in duration-300">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-sm uppercase tracking-wider">Select Options</h4>
                <button onClick={() => setIsVariantSelectorOpen(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {product.variants?.map((variant) => (
                  <div key={variant.id}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">{variant.name}</p>
                    <div className="flex flex-wrap gap-2">
                      {variant.options.map((option) => (
                        <button
                          key={option}
                          onClick={() => setSelectedVariants(prev => ({ ...prev, [variant.name]: option }))}
                          className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                            selectedVariants[variant.name] === option
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'border-slate-200 dark:border-slate-700 hover:border-indigo-500'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={confirmQuickAdd}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Confirm Add
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <p className="text-xs text-indigo-600 dark:text-indigo-400 font-bold uppercase tracking-wider mb-1">
            {product.category}
          </p>
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2 truncate group-hover:text-indigo-600 transition-colors">
            {product.name}
          </h3>
          
          {/* Rating */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(product.rating) ? 'fill-current' : ''}`} />
              ))}
            </div>
            <span className="text-xs text-slate-400">({product.reviews})</span>
          </div>

          {/* Pricing */}
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">${product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="text-sm text-slate-400 line-through">${product.originalPrice.toFixed(2)}</span>
              )}
            </div>
            <button 
              onClick={handleQuickAdd}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-all"
            >
              QUICK ADD
            </button>
          </div>
        </div>
      </div>

      {/* Quick View Modal */}
      {isQuickViewOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setIsQuickViewOpen(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-[2rem] shadow-2xl flex flex-col md:flex-row animate-in fade-in zoom-in duration-300">
            <button 
              onClick={() => setIsQuickViewOpen(false)}
              className="absolute top-6 right-6 z-10 p-2 bg-slate-100 dark:bg-slate-800 hover:bg-rose-500 hover:text-white rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left: Images */}
            <div className="w-full md:w-1/2 p-4 md:p-8 bg-slate-50 dark:bg-slate-800/50 flex flex-col">
              <div className="aspect-square rounded-2xl overflow-hidden bg-white dark:bg-slate-900 mb-4 border border-slate-100 dark:border-slate-800">
                <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {product.images.map((img, idx) => (
                  <div key={idx} className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-indigo-500 cursor-pointer transition-all">
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Content Area with Scrolling */}
            <div className="w-full md:w-1/2 flex flex-col overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b dark:border-slate-800">
                <button 
                  onClick={() => setActiveTab('details')}
                  className={`flex-1 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === 'details' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                >
                  Product Details
                </button>
                <button 
                  onClick={() => setActiveTab('reviews')}
                  className={`flex-1 py-4 text-sm font-bold uppercase tracking-widest border-b-2 transition-all flex items-center justify-center gap-2 ${activeTab === 'reviews' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                >
                  Reviews ({product.reviewsList?.length || 0})
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 md:p-10">
                {activeTab === 'details' ? (
                  <div className="flex flex-col h-full animate-in slide-in-from-right-4 duration-300">
                    <div className="mb-6">
                      <span className="text-indigo-600 font-bold uppercase tracking-widest text-xs mb-2 block">{product.category}</span>
                      <h2 className="text-3xl font-bold font-serif mb-4 leading-tight">{product.name}</h2>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex text-amber-400">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-current' : ''}`} />
                          ))}
                        </div>
                        <span className="text-sm text-slate-500">{product.rating} Rating ({product.reviews} Reviews)</span>
                      </div>
                      <div className="flex items-baseline gap-4 mb-6">
                        <span className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">${product.price.toFixed(2)}</span>
                        {product.originalPrice && (
                          <span className="text-xl text-slate-400 line-through">${product.originalPrice.toFixed(2)}</span>
                        )}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
                        {product.description}
                      </p>
                    </div>

                    {product.variants && product.variants.length > 0 && (
                      <div className="mb-8 space-y-6">
                        {product.variants.map((variant) => (
                          <div key={variant.id}>
                            <h4 className="font-bold text-sm uppercase tracking-wider mb-3">{variant.name}</h4>
                            <div className="flex flex-wrap gap-3">
                              {variant.options.map((option) => (
                                <button
                                  key={option}
                                  onClick={() => setSelectedVariants(prev => ({ ...prev, [variant.name]: option }))}
                                  className={`px-4 py-2 rounded-xl border-2 font-medium transition-all ${
                                    selectedVariants[variant.name] === option
                                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600'
                                      : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700'
                                  }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mb-8 p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl">
                      <h4 className="font-bold text-sm uppercase tracking-wider mb-3">Specifications</h4>
                      <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                        {Object.entries(product.specs).map(([key, val]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-[10px] text-slate-400 uppercase font-bold">{key}</span>
                            <span className="text-sm font-medium">{val}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 mb-8">
                      <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                        <Check className="w-4 h-4" /> In Stock (Only {product.stock} left)
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Truck className="w-4 h-4" /> Free Express Shipping
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Shield className="w-4 h-4" /> 2-Year Official Warranty
                      </div>
                    </div>

                    <div className="flex gap-4 mt-auto">
                      <button 
                        onClick={() => {
                          if (product.variants && product.variants.length > 0) {
                            const allSelected = product.variants.every(v => selectedVariants[v.name]);
                            if (!allSelected) {
                              alert('Please select all options');
                              return;
                            }
                          }
                          addToCart(product, selectedVariants);
                          setIsQuickViewOpen(false);
                          setSelectedVariants({});
                        }}
                        className="flex-1 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold flex items-center justify-center gap-2 transition-all"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col h-full animate-in slide-in-from-left-4 duration-300">
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-indigo-600" />
                      Customer Experiences
                    </h3>
                    
                    {product.reviewsList && product.reviewsList.length > 0 ? (
                      <div className="space-y-6">
                        {product.reviewsList.map((review) => (
                          <div key={review.id} className="p-5 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 font-bold">
                                  {review.user.charAt(0)}
                                </div>
                                <div>
                                  <h5 className="font-bold text-slate-800 dark:text-slate-200">{review.user}</h5>
                                  <div className="flex text-amber-400">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'fill-current' : ''}`} />
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-slate-400 uppercase font-bold">
                                <Calendar className="w-3 h-3" />
                                {review.date}
                              </div>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 italic">
                              "{review.comment}"
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 py-12">
                        <MessageSquare className="w-12 h-12 mb-4 opacity-20" />
                        <p>No reviews yet. Be the first to share your thoughts!</p>
                      </div>
                    )}

                    <button className="mt-8 w-full py-4 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 text-indigo-600 rounded-2xl font-bold transition-all">
                      Write a Review
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
